import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Modal } from './Common';

/**
 * Log streaming for one scope — a client, or the provider itself.
 *
 * A service account here is not a user account. It authenticates against the
 * export endpoint and against nothing else in the application, so a collector
 * holding one of these tokens can read the audit log and cannot read, change
 * or reach anything else. That is why creating one does not need any of the
 * care that creating a user does.
 *
 * `scope` is a client id, or the string 'msp' for provider-wide accounts.
 */

const relative = (iso) => {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
};

function Field({ label, value, mono }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <code style={{
          flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--bg-input)', fontSize: mono ? 12 : 13, wordBreak: 'break-all',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}>{value}</code>
        <Button variant="secondary" style={{ padding: '6px 12px', fontSize: 12, whiteSpace: 'nowrap' }}
          onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
    </div>
  );
}

export default function LogStreaming({ scope, scopeLabel }) {
  const { apiFetch, hasGroup } = useAuth();
  const isProviderScope = scope === 'msp';
  // Only a Super Admin can create an account that reads every tenant.
  const canManage = isProviderScope ? hasGroup('Super Admins')
    : (hasGroup('Client Admins') || hasGroup('Super Admins'));

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', expiresDays: '' });
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);
  const [showSetup, setShowSetup] = useState(false);

  const exportUrl = `${window.location.origin}/api/logs/export`;

  const load = useCallback(async () => {
    setLoading(true);
    const query = isProviderScope ? '?clientId=provider' : `?clientId=${scope}`;
    try {
      const res = await apiFetch(`/logs/service-accounts${query}`);
      const d = await res.json().catch(() => null);
      setAccounts(res.ok && Array.isArray(d?.accounts) ? d.accounts : []);
      if (!res.ok) setError(d?.error || '');
    } catch {
      setAccounts([]);
    }
    setLoading(false);
  }, [apiFetch, scope, isProviderScope]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    setError('');
    const body = {
      name: form.name,
      description: form.description || null,
      expiresDays: form.expiresDays || null,
    };
    if (!isProviderScope) body.clientId = scope;
    const res = await apiFetch('/logs/service-accounts', { method: 'POST', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) { setError(d.error || 'Could not create the account'); return; }
    setShowModal(false);
    setForm({ name: '', description: '', expiresDays: '' });
    // Shown once and never again: only the hash is stored.
    setIssued(d);
    load();
  };

  const revoke = async (account) => {
    if (!window.confirm(
      `Revoke "${account.name}"? Any collector using this token stops receiving logs immediately.`
    )) return;
    const res = await apiFetch(`/logs/service-accounts/${account.id}`, { method: 'DELETE' });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || 'Could not revoke the account');
      return;
    }
    load();
  };

  const active = accounts.filter(a => a.is_active);
  const revoked = accounts.filter(a => !a.is_active);

  return (
    <Card style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Log streaming</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Pull the audit log into Splunk, or anything else that can poll an HTTP endpoint.
            {isProviderScope
              ? ' A provider account reads every client.'
              : ` An account here reads ${scopeLabel || 'this client'}'s activity and nothing else.`}
          </p>
        </div>
        <Button variant="secondary" style={{ padding: '6px 14px', fontSize: 13 }}
          onClick={() => setShowSetup(s => !s)}>
          {showSetup ? 'Hide setup' : 'Setup guide'}
        </Button>
        {canManage && (
          <Button onClick={() => { setError(''); setShowModal(true); }} style={{ padding: '6px 14px', fontSize: 13 }}>
            New service account
          </Button>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca',
                      color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>
      )}

      <Field label="Endpoint" value={exportUrl} />

      {showSetup && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
            The endpoint returns newline-delimited JSON, one event per line, oldest first. Each response
            carries an <code>X-Next-Cursor</code> header; pass it back as <code>?cursor=</code> on the next
            request and the collector resumes exactly where it stopped — no gaps, no repeats.
          </p>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
            Splunk — a REST input, or a scripted poll
          </div>
          <pre style={{
            padding: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)',
            fontSize: 12, overflowX: 'auto', marginBottom: 12,
          }}>{`curl -s "${exportUrl}?limit=1000&cursor=$CURSOR" \\
  -H "Authorization: Bearer $TOKEN" -D headers.txt

# sourcetype = _json, and the event time is the "time" field
CURSOR=$(awk -F': ' '/X-Next-Cursor/{print $2}' headers.txt | tr -d '\\r')`}</pre>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
            Parameters
          </div>
          <ul style={{ fontSize: 13, color: 'var(--text-secondary)', paddingLeft: 18, lineHeight: 1.7 }}>
            <li><code>cursor</code> — resume after this event. Preferred over <code>since</code> for polling.</li>
            <li><code>since</code>, <code>until</code> — ISO 8601 timestamps, for a one-off backfill.</li>
            <li><code>limit</code> — up to 5000, default 500.</li>
            <li><code>action</code>, <code>category</code> — narrow the feed.</li>
            <li><code>format=json</code> — a single object with <code>events</code> and <code>next_cursor</code>,
                for collectors that will not read NDJSON.</li>
          </ul>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      ) : accounts.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          No service accounts yet. {canManage ? 'Create one to give a collector access.' : ''}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-input)' }}>
                {['Name', 'Token', 'Last used', 'Requests', 'Expires', ''].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                                       textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...active, ...revoked].map(a => (
                <tr key={a.id} style={{ borderTop: '1px solid var(--border)', opacity: a.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 600 }}>
                      {a.name}
                      {!a.is_active && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '1px 6px',
                                       borderRadius: 8, background: '#6b7280', color: '#fff' }}>REVOKED</span>
                      )}
                    </div>
                    {a.description && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{a.description}</div>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', fontFamily: 'ui-monospace, monospace', fontSize: 12,
                               color: 'var(--text-secondary)' }}>{a.token_prefix}…</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>
                    {relative(a.last_used_at)}
                    {a.last_used_ip && (
                      <div style={{ fontSize: 11 }}>{a.last_used_ip}</div>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{a.request_count || 0}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>
                    {a.expires_at ? new Date(a.expires_at).toLocaleDateString() : 'never'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {canManage && a.is_active && (
                      <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => revoke(a)}>Revoke</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="New log service account" width={520}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
          This account can read {isProviderScope ? 'every client\u2019s' : `${scopeLabel || 'this client'}\u2019s`} audit
          log through the export endpoint. It cannot sign in, and it cannot reach any other part of the application.
        </p>
        <Input label="Name *" value={form.name} placeholder="Splunk collector"
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <Input label="Description" value={form.description} placeholder="What is collecting these, and who owns it"
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        <Input label="Expires after (days)" type="number" value={form.expiresDays}
          placeholder="Leave empty for no expiry"
          onChange={e => setForm(f => ({ ...f, expiresDays: e.target.value }))} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={create} disabled={saving || !form.name.trim()}>
            {saving ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={!!issued} onClose={() => setIssued(null)} title="Token created" width={560} noOverlayClose>
        <div style={{ padding: 10, borderRadius: 6, background: '#fffbeb', border: '1px solid #fcd34d',
                      color: '#92400e', fontSize: 13, marginBottom: 14 }}>
          Copy this now. Only a hash is stored, so it cannot be shown again — if it is lost, revoke the
          account and create another.
        </div>
        {issued && <Field label="Token" value={issued.token} mono />}
        <Field label="Endpoint" value={exportUrl} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Button onClick={() => setIssued(null)}>Done</Button>
        </div>
      </Modal>
    </Card>
  );
}
